import * as React from 'react';
import PropTypes from 'prop-types';
import { useState, Fragment, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { MenuItem } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { PAGES_DATA, USER_TYPE } from '../../../../utils/constants';
import styles from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { NavSubMenuItems } from '../NavSubMenu';
import { listFirebasePayCollections } from '../../../../store/actions/firebasePay';
import { listFirebaseDrillCollections } from '../../../../store/actions/firebaseDrill';
import { getVersionDetails } from '../../../../store/actions/authorization';

const hideLabelsFromMenu = ['Field Tickets', 'TruFlowSettings', 'TransactionHistory KPIs', 'CheckIns KPIs', 'FieldBoxes KPIs', 'Marketshare KPIs', 'Data'];

const renderMenuLabel = (label, userType) =>
  label === 'Shop'
    ? userType === USER_TYPE.CUSTOMER_MANAGER ||
        userType === USER_TYPE.CUSTOMER_ADMIN ||
        userType === USER_TYPE.CUSTOMER_USER ||
        userType === USER_TYPE.CUSTOMER_RIG
      ? label
      : 'Inventory'
    : label === 'Companies'
      ? userType === USER_TYPE.SELLER_MARKETPLACE_ADMIN
        ? 'Customers'
        : userType === USER_TYPE.CUSTOMER_USER ||
          userType === USER_TYPE.CUSTOMER_RIG ||
          userType === USER_TYPE.CUSTOMER_MANAGER ||
          userType === USER_TYPE.CUSTOMER_ADMIN
          ? 'Sellers'
          : label
      : label === 'Users'
        ? userType !== USER_TYPE.SELLER_MARKETPLACE_ADMIN
          ? label
          : 'Transaction History'
        : label;

const NavMenu = ({
  user,
  handleCloseNav,
  setSubMenuOpen,
  openSubMenu,
  selectedIndex,
  setSelectedIndex,
}) => {
  const classes = styles();
  const dispatch = useDispatch();
  const [pages, setPages] = useState(PAGES_DATA);
  const categories = useSelector((state) => state.parts?.categories);
  const [searchParams] = useSearchParams();
  const category = searchParams.get('cat');
  const location = useLocation();
  const firebasePayCollections = useSelector((state) => state?.firebasePay?.firebasePayCollections);
  const firebaseDrillCollections = useSelector((state) => state?.firebaseDrill?.firebaseDrillCollections);
  const appVersionDetails = useSelector((state) => state.currentUser.appVersionDetails);
  const versionItems = React.useMemo(
    () => [
      { label: `Frontend: ${appVersionDetails?.data?.FE?.version}` },
      { label: `Backend: ${appVersionDetails?.data?.BE?.version}` },
      { label: `Hydraulics: ${appVersionDetails?.data?.PH?.version}` },
    ],
    [appVersionDetails]
  );

  useEffect(() => {
    if (!appVersionDetails?.data?.FE) {
      dispatch(getVersionDetails());
    }
    if (user.type === USER_TYPE.PREMIUM_ADMIN && !firebasePayCollections?.data?.length && !firebaseDrillCollections?.data?.length) {
      dispatch(listFirebasePayCollections());
      dispatch(listFirebaseDrillCollections());
    }
  }, []);

  useEffect(() => {
    addSubmenuItems();
  }, [categories?.data]);

  useEffect(() => {
    if (categories?.loading) return;

    let updatedPages = user.type === USER_TYPE.PREMIUM_SELECT_ADMIN
      ? PAGES_DATA.filter(
        page =>
          page.roles &&
          page.roles.some(role => user.accessLevels?.includes(role))
      )
      : [...PAGES_DATA];

    const isPremiumRealtimeAdmin = user.type === USER_TYPE.PREMIUM_REALTIME_MPD_ADMIN || (user.type === USER_TYPE.PREMIUM_SELECT_ADMIN && user.accessLevels?.includes(USER_TYPE.PREMIUM_REALTIME_MPD_ADMIN));

    updatedPages = updatedPages?.map(page => {
      if (page?.label !== 'Server-Side') return page;

      if (user.type === USER_TYPE.PREMIUM_ADMIN) {
        return page;
      }

      if (!isPremiumRealtimeAdmin) {
        return null;
      }

      return {
        ...page,
        submenu: page.submenu?.filter(sub => sub.label === 'Enverus'),
      };
    });

    updatedPages = updatedPages?.map(page => {
      if (page?.label !== 'About') return page;

      return {
        ...page,
        submenu: page.submenu?.map(sub => {
          if (sub.label !== 'Versions') return sub;

          return {
            ...sub,
            submenu: versionItems,
          };
        }),
      };
    });

    updatedPages = updatedPages.filter(Boolean);
    setPages(updatedPages);
  }, [categories?.loading, user, versionItems]);

  useEffect(() => {
    if (firebasePayCollections?.data?.length && firebaseDrillCollections?.data?.length) {
      addFirebaseSubMenuItems('Pay', firebasePayCollections.data, 'Firebase');
      addFirebaseSubMenuItems('Drill', firebaseDrillCollections.data, 'Firebase');
    }
  }, [firebasePayCollections, firebaseDrillCollections]);

  const addFirebaseSubMenuItems = (label, data, urlPrefix) => {
    setPages(prevPages => {
      return prevPages.map(page => {
        if (page.label !== 'Server-Side') return page;
        const newPage = { ...page };
        function findByLabel(obj, targetLabel) {
          if (obj.label === targetLabel) return obj;

          if (Array.isArray(obj.submenu)) {
            for (const item of obj.submenu) {
              const result = findByLabel(item, targetLabel);
              if (result) return result;
            }
          }

          return null;
        }
        const firebaseObj = findByLabel(newPage, 'Firebase');

        firebaseObj.submenu = (firebaseObj.submenu || []).map(sub => {
          if (sub.label !== label) return sub;
          return {
            ...sub,
            submenu: data.map(item => ({
              label: item,
              link: `/${urlPrefix}/${label}?col=${item}`,
              parentLabel: label
            })),
          };
        });

        return newPage;
      });
    });
  };

  const addSubmenuItems = () => {
    if (categories?.data.length > 0) {
      const partsItemIndex = pages.findIndex((page) => page.label === 'Shop');
      const partsPageMeta = pages.find((page) => page.label === 'Shop');
      const companyId = searchParams.get('companyId');
      const vendorId = searchParams.get('vendorId');
      const name = searchParams.get('name');
      const customerRigUserId = searchParams.get('customerRigUser');
      // keep query params for companies and sellers
      let queryParams = '';
      if (companyId) {
        queryParams = queryParams + `&companyId=${encodeURIComponent(companyId)}`;
      }
      if (vendorId) {
        queryParams = queryParams + `&vendorId=${encodeURIComponent(vendorId)}`;
      }
      if (name) {
        queryParams = queryParams + `&name=${encodeURIComponent(name)}`;
      }
      if (customerRigUserId) {
        queryParams = queryParams + `&customerRigUser=${encodeURIComponent(customerRigUserId)}`;
      }

      if (partsPageMeta) {
        partsPageMeta.submenu = [];
        partsPageMeta.submenu.push({
          label: 'ALL',
          link: '/Shop?' + queryParams,
        });
        categories?.data.forEach((categoryData) => {
          const link = 'Shop?cat=' + categoryData + queryParams;
          partsPageMeta.submenu.push({
            label: categoryData,
            link,
          });
        });
        setPages((state) => [
          ...state.toSpliced(partsItemIndex, 1, partsPageMeta),
        ]);
      }
    }
  };

  const handleSubmenuToggle = (index, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (selectedIndex === index) {
      setSelectedIndex(null);
    } else {
      setSelectedIndex(index);
    }
  };

  const handleClickSubMenu = () => {
    if (searchParams.size === 0) {
      setSubMenuOpen(!openSubMenu);
      handleCloseNav();
      return;
    }
    window.location.reload(false);
  };

  return (
    <>
      {pages.map((page, index) => {
        const showPage =
          ((page.roles?.includes('*') && page.label) ||
            page.roles?.includes(user.type)) &&
          page.label &&
          !hideLabelsFromMenu.includes(page.label);

        if (!showPage) return null;

        const hasActiveSubmenu = (menuItem, pathname) => {
          if (!menuItem.submenu) return false;

          return menuItem.submenu.some((sub) => {
            if (sub.link && pathname.startsWith(sub.link)) {
              return true;
            }
            return hasActiveSubmenu(sub, pathname);
          });
        };

        const isParentWithSubmenu = page.submenu && Array.isArray(page.submenu);
        const isSelected = page.link && location.pathname === page.link;
        const isSubActive = isParentWithSubmenu && hasActiveSubmenu(page, location.pathname);

        return (
          <Fragment key={`fragment_main_menu_${page.label}${index}`}>
            {page.label === 'About' && <hr />}
            <MenuItem
              key={`main_menu_${page.label}`}
              selected={isSelected || isSubActive}
              onClick={() =>
                page.submenu ? handleSubmenuToggle(index) : handleClickSubMenu()
              }
              className={classes.menuItem}
            >
              {page.submenu
                ? (
                  <span className={classes.navMenuItem}>
                    {renderMenuLabel(page.label, user.type)}
                  </span>
                  )
                : (
                  <Link
                    key={`main_menu_link_${page.label}`}
                    to={page.link}
                    className={classes.navMenuItem}
                  >
                    {renderMenuLabel(page.label, user.type)}
                  </Link>
                  )}
              {page.submenu
                ? (
                    index === selectedIndex ? <ExpandLess /> : <ExpandMore />
                  )
                : null}
            </MenuItem>
            {page.submenu
              ? (
                <NavSubMenuItems
                  parentLabel={page.label}
                  subMenu={page.submenu}
                  index={index}
                  selectedIndex={selectedIndex}
                  handleCloseNav={handleCloseNav}
                  category={category}
                />
                )
              : null}
          </Fragment>
        );
      })}
    </>
  );
};

NavMenu.propTypes = {
  user: PropTypes.object.isRequired,
  handleCloseNav: PropTypes.func.isRequired,
  setSubMenuOpen: PropTypes.func.isRequired,
  openSubMenu: PropTypes.bool.isRequired,
  selectedIndex: PropTypes.number,
  setSelectedIndex: PropTypes.func,
};

export default NavMenu;
